import { useState, useEffect } from "react";

import { useLanguage } from "@/providers/LanguageProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ErrorCallout } from "@/components/ui/error-callout";
import type { IntakeData } from "@/lib/types";
import { Loader2, UserCheck, Lock } from "lucide-react";
import { scrollToFirstError } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CountrySelect } from "@/components/form/CountrySelect";
 
import { Trans} from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { Link } from "react-router-dom";



interface WelcomeStepProps {
  data: IntakeData;
  onUpdate: (data: Partial<IntakeData>) => void;
  onEnsureDraft: (data: Partial<IntakeData>) => Promise<void>;
  onNext: () => void;
}

export default function WelcomeStep({ 
  data,
  onUpdate,
  onEnsureDraft,
  onNext,
}: WelcomeStepProps) {
  const { i18n } = useLingui();

  const { setLocale, locale } = useLanguage();
  const [formData, setFormData] = useState({
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    phone: data.phone,
    country: data.country,
    preferredLanguage: data.preferredLanguage || locale,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [formErrorStatus, setFormErrorStatus] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
 

  // Sync preferredLanguage with locale when locale changes (e.g., on page reload)
  useEffect(() => {
    // Only sync if locale is available and different from current preferredLanguage
    // Also ensure data.preferredLanguage is empty/undefined to avoid overwriting user's saved preference
    if (locale && (!data.preferredLanguage || data.preferredLanguage !== locale)) {
      setFormData((prev) => {
        // Only update if the current value doesn't match the locale
        if (prev.preferredLanguage !== locale) {
          return { ...prev, preferredLanguage: locale };
        }
        return prev;
      });
    }
  }, [locale, data.preferredLanguage]); // Sync when locale or initial data changes

  const handleInputChange = (field: string, value: string) => {
   
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handlePreferredLanguageChange = async (value: "en" | "es") => {
    handleInputChange("preferredLanguage", value);
    try {
      await setLocale(value);
    } catch (error) {
      console.error("Failed to switch locale", error);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = i18n._(`First name is required`) ;
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName =i18n._(`Last name is required`);
    }
    if (!formData.email.trim()) {
      newErrors.email = i18n._(`Email is required`); 
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = i18n._(`Please enter a valid email`);  
    }
    if (!formData.phone.trim()) {
      newErrors.phone = i18n._(`Phone number is required`);  
    }
    if (!formData.country) {
      newErrors.country = i18n._(`Country is required`);    
    }

    setErrors(newErrors);
    
    // Find first error field and scroll to it
    if (Object.keys(newErrors).length > 0) {
      scrollToFirstError();
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = async () => {      
    if (isSubmitting) return;
    if (!validateForm()) return;
    setIsSubmitting(true);
    setFormError(null);
    setFormErrorStatus(null);

    try {
      onUpdate(formData);
      await onEnsureDraft(formData);
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => {
        onNext();
      }, 100);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
 
          : i18n._(`Failed to prepare your intake form. Please try again.`) ;
 
      setFormError(message);
      setFormErrorStatus(
        error instanceof Error
          ? Number((error as { status?: number }).status || 0) || null
          : null,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full flex flex-col lg:justify-center">
      
      {/* Mobile: Traditional layout, elevated to Premium */}
      <div className="lg:hidden space-y-2">
        {/* Welcome Message */}
        <div className="text-center space-y-4 px-4 pt-4">
          <div>
            <h2 className="heading-secondary text-gray-900 mb-2 mt-4">
              <Trans>
                Welcome to Your <span className="text-[#166B93]">Premium</span>{" "}
                Medical Journey
              </Trans>
            </h2>
            <p className="text-[#FF9142] font-heading font-bold text-lg mb-4">
              <Trans>Medical Care Without Borders</Trans>
            </p>
            <div className="bg-[#166B93]/5 border border-[#166B93]/10 rounded-2xl p-5 mb-4 shadow-[0_4px_20px_rgb(22,107,147,0.05)]">
              <p className="text-gray-700 text-sm leading-relaxed font-medium">
                <Trans>
                  <strong>Experience world-class medical care!</strong> This quick
                  5-minute process gathers your essential information, enabling our
                  expert medical team to provide you with
                  <em> personalized consultation and treatment recommendations</em>.
                </Trans>
              </p>
            </div>
          </div>
        </div>

       

        {/* Contact Information Form */}
        <div className="space-y-5 bg-white px-4 pb-4">
          <div className="flex items-center gap-2 mb-3">
            <UserCheck className="w-4 h-4 text-gray-500" />
            <h3 className="font-heading font-semibold text-gray-900 text-base">
 
              <Trans>Your Information</Trans>
 
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label
                htmlFor="firstName"
                className="text-sm font-semibold text-gray-700 mb-1.5 block"
              >
 
                <Trans>First Name *</Trans>
 
              </Label>
              <Input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
                className={`h-12 text-base rounded-xl border-2 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-[#166B93]/10 hover:border-gray-300 transition-all duration-300 ${errors.firstName ? "border-red-500 focus:border-red-500" : "border-gray-200 focus:border-[#166B93]"}`}
                placeholder={i18n._(`First name`) }
              />
              {errors.firstName && (
                <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.firstName}</p>
              )}
            </div>
            <div>
              <Label
                htmlFor="lastName"
                className="text-sm font-semibold text-gray-700 mb-1.5 block"
              >
 
                <Trans>Last Name *</Trans>
 
              </Label>
              <Input
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
                className={`h-12 text-base rounded-xl border-2 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-[#166B93]/10 hover:border-gray-300 transition-all duration-300 ${errors.lastName ? "border-red-500 focus:border-red-500" : "border-gray-200 focus:border-[#166B93]"}`}
 
                placeholder={i18n._(`Last name`) } 
 
              />
              {errors.lastName && (
                <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.lastName}</p>
              )}
            </div>
          </div>

          <div>
            <Label
              htmlFor="email"
              className="text-sm font-semibold text-gray-700 mb-1.5 block"
            >
 
              <Trans>Email Address *</Trans>
 
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              className={`h-12 text-base rounded-xl border-2 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-[#166B93]/10 hover:border-gray-300 transition-all duration-300 ${errors.email ? "border-red-500 focus:border-red-500" : "border-gray-200 focus:border-[#166B93]"}`}
              placeholder={i18n._(`your@email.com`) }  
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.email}</p>
            )}
          </div>

          <div>
            <Label
              htmlFor="phone"
              className="text-sm font-semibold text-gray-700 mb-1.5 block"
            >
 
              <Trans>Phone Number *</Trans>
 
            </Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              className={`h-12 text-base rounded-xl border-2 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-[#166B93]/10 hover:border-gray-300 transition-all duration-300 ${errors.phone ? "border-red-500 focus:border-red-500" : "border-gray-200 focus:border-[#166B93]"}`}
              placeholder={i18n._(`+1 (555) 123-4567`) }   
            />
            {errors.phone && (
              <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.phone}</p>
            )}
          </div>



            {/* Country Select- Mobile View */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="country" className="text-sm font-medium text-gray-700">
                <Trans>Country *</Trans>
              </Label>
              <CountrySelect
                value={formData.country}
                onValueChange={(value) => handleInputChange("country", value)}
                placeholder={i18n._(`Select country`)}
                className={`mt-1 ${
                  errors.country ? "border-red-500" : ""
                }`}
                aria-invalid={!!errors.country}
                aria-describedby={errors.country ? "country-error" : undefined}
              />
              {errors.country && (
                <p id="country-error" className="text-red-500 text-xs mt-1">
                  {errors.country}
                </p>
              )}
            </div>


          {/*  */}
            <div>
              <Label htmlFor="language" className="text-sm font-medium text-gray-700">
                <Trans>Preferred Language *</Trans>
              </Label>
              <Select
                value={formData.preferredLanguage}
                onValueChange={(value: "en" | "es") => {
                  void handlePreferredLanguageChange(value);
                }}>
                <SelectTrigger className="mt-1 h-12 text-base rounded-lg border-2 border-gray-200 focus:border-[#166B93] transition-colors">
                  <SelectValue placeholder={i18n._(`Select language`) }    />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200 shadow-lg">
                  <SelectItem value="en" className="hover:bg-[#166B93]/10 focus:bg-[#166B93]/10">
                    <Trans>English</Trans>
                  </SelectItem>
                  <SelectItem value="es" className="hover:bg-[#166B93]/10 focus:bg-[#166B93]/10">
                    <Trans>Español (Spanish)</Trans>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {/*  */}





          {/* Privacy Notice */}
          <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
            <p>
 
              <Trans>
                Your information is encrypted and secure. We only use it for your
                consultation and follow strict privacy guidelines.{" "}
                <Link
                  to="privacy-policy"
                  target="_blank"
                  className="text-[#166B93] underline hover:text-[#166B93]/80"
                >
                  Privacy Policy
                </Link>
              </Trans>
 
            </p>
          </div>


          {/* Continue Button */}
          <div className="space-y-2 pt-2">
            <Button
              onClick={handleContinue}
              className="w-full shadow-[0_4px_15px_rgba(22,107,147,0.3)] hover:shadow-[0_8px_25px_rgba(22,107,147,0.4)] transform hover:-translate-y-0.5 transition-all duration-300 bg-[#166B93] hover:bg-[#105373] text-white font-bold h-14 rounded-xl text-lg"
              size="lg"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="animate-spin mr-2" />
 
                    <Trans>Creating your profile...</Trans>
                </>
              ) : (
                  <Trans>Begin Your Medical Journey</Trans>
 
              )}
            </Button>
            {formError ? (
              <ErrorCallout
                message={formError}
                status={formErrorStatus}
                className="text-left"
              />
            ) : null}
          </div>
        </div>
      </div>

      {/* Desktop/Tablet: Modern full-screen layout */}
      <div className="hidden lg:block max-w-2xl xl:max-w-4xl mx-auto">
        <div className="space-y-8 xl:space-y-12">
          {/* Welcome Message - Centered and prominent */}
          <div className="text-center space-y-6">
            <h2 className="heading-primary text-gray-900 leading-tight">
 
              <Trans>
                Welcome to Your <span className="text-[#166B93]">Premium</span>{" "}
                Medical Journey
              </Trans>
            </h2>
            <p className="heading-tertiary text-[#FF9142] font-semibold">
              <Trans>Medical Care Without Borders</Trans>
            </p>
            <p className="body-base text-gray-600 max-w-2xl mx-auto">
              <Trans>
                Experience world-class medical care with our expert team. Let's start with your information.
              </Trans>
 
            </p>
          </div>

          {/* Security Banner
          <div className="bg-gradient-to-r from-[#166B93]/10 to-[#CEF12F]/10 rounded-2xl p-6 border border-[#166B93]/20">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="w-12 h-12 bg-[#166B93] rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div className="text-center">
                <h3 className="heading-tertiary text-[#166B93] mb-1">
                  <Trans>Your Privacy &amp; Security</Trans>
                </h3>
                <p className="body-small text-gray-600">
                  <Trans>HIPAA compliant &amp; end-to-end encrypted</Trans>
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div className="flex items-center justify-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#CEF12F]" />
                <span className="text-sm text-gray-700">
                  <Trans>End-to-end encryption</Trans>
                </span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#CEF12F]" />
                <span className="text-sm text-gray-700">
                  <Trans>HIPAA compliant</Trans>
                </span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#CEF12F]" />
                <span className="text-sm text-gray-700">
                  <Trans>Never shared without consent</Trans>
                </span>
              </div>
            </div>
          </div> */}

          {/* Contact Information Form - Modern card-like sections */}
          <div className="bg-white rounded-3xl shadow-[0_8px_40px_rgb(0,0,0,0.06)] ring-1 ring-gray-900/5 p-8 xl:p-10 space-y-8">
            <div className="text-center mb-6">
              <h3 className="heading-tertiary text-gray-900 mb-2">
 
                <Trans>Your Information</Trans>
              </h3>
              <p className="body-base text-gray-600">
                <Trans>Please provide your details to get started</Trans>
 
              </p>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 xl:gap-8">
              <div>
                <Label
                  htmlFor="firstName"
                  className="text-base font-semibold text-gray-700 mb-2 block"
                >
 
                  <Trans>First Name *</Trans>
 
                </Label>
                <Input
                  id="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  className={`h-14 text-base rounded-xl border-2 bg-gray-50/50 hover:border-gray-300 focus:bg-white focus:ring-4 focus:ring-[#166B93]/10 transition-all duration-300 ${errors.firstName ? "border-red-500 focus:border-red-500" : "border-gray-200 focus:border-[#166B93]"}`}
 
                  placeholder={i18n._(`Enter your first name`) }  
 
                />
                {errors.firstName && (
                  <p className="text-red-500 text-sm mt-2 font-medium">
                    {errors.firstName}
                  </p>
                )}
              </div>
              <div>
                <Label
                  htmlFor="lastName"
                  className="text-base font-semibold text-gray-700 mb-2 block"
                >
 
                  <Trans>Last Name *</Trans>
 
                </Label>
                <Input
                  id="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                  className={`h-14 text-base rounded-xl border-2 bg-gray-50/50 hover:border-gray-300 focus:bg-white focus:ring-4 focus:ring-[#166B93]/10 transition-all duration-300 ${errors.lastName ? "border-red-500 focus:border-red-500" : "border-gray-200 focus:border-[#166B93]"}`}
 
                  placeholder={i18n._(`Enter your last name`) }  
 
                />
                {errors.lastName && (
                  <p className="text-red-500 text-sm mt-2 font-medium">{errors.lastName}</p>
                )}
              </div>
              <div>
                <Label
                  htmlFor="email"
                  className="text-base font-semibold text-gray-700 mb-2 block"
                >
 
                  <Trans>Email Address *</Trans>
 
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className={`h-14 text-base rounded-xl border-2 bg-gray-50/50 hover:border-gray-300 focus:bg-white focus:ring-4 focus:ring-[#166B93]/10 transition-all duration-300 ${errors.email ? "border-red-500 focus:border-red-500" : "border-gray-200 focus:border-[#166B93]"}`}
                  placeholder={i18n._(`your@email.com`) } 
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-2 font-medium">{errors.email}</p>
                )}
              </div>
              <div>
                <Label
                  htmlFor="phone"
                  className="text-base font-semibold text-gray-700 mb-2 block"
                >
 
                  <Trans>Phone Number *</Trans>
 
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  className={`h-14 text-base rounded-xl border-2 bg-gray-50/50 hover:border-gray-300 focus:bg-white focus:ring-4 focus:ring-[#166B93]/10 transition-all duration-300 ${errors.phone ? "border-red-500 focus:border-red-500" : "border-gray-200 focus:border-[#166B93]"}`}
 
                  placeholder={i18n._(`+1 (555) 123-4567`) }  
                />
 
                {errors.phone && (
                  <p className="text-red-500 text-sm mt-2 font-medium">{errors.phone}</p>
                )}
              </div>


              {/* Country Select- Desktop View */}
              <div>
                <Label htmlFor="country" className="text-base font-semibold text-gray-700 mb-2 block">
                  <Trans>Country *</Trans>
                </Label>
                <CountrySelect
                  value={formData.country}
                  onValueChange={(value) => handleInputChange("country", value)}
                  placeholder={i18n._(`Select country`)}
                  buttonClassName="h-14"
                  aria-invalid={!!errors.country}
                  aria-describedby={errors.country ? "country-error-desktop" : undefined}
                />
                {errors.country && (
                  <p
                    id="country-error-desktop"
                    className="text-red-500 text-sm mt-2 font-medium"
                  >
                    {errors.country}
                  </p>
                )}
              </div>
 
              {/* Preferred Language Select */}
              <div>
                <Label htmlFor="language" className="text-base font-semibold text-gray-700 mb-2 block">
 
                  <Trans>Preferred Language *</Trans>
 
                </Label>
                <Select
                  value={formData.preferredLanguage}
                  onValueChange={(value: "en" | "es") => {
                    void handlePreferredLanguageChange(value);
                  }}
                >
 
                  <SelectTrigger className="h-14 text-base rounded-xl border-2 border-gray-200 bg-gray-50/50 hover:border-gray-300 focus:bg-white focus:ring-4 focus:ring-[#166B93]/10 focus:border-[#166B93] transition-all duration-300">
                    <SelectValue placeholder={i18n._(`Select language`) }
                       />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 shadow-[0_8px_30px_rgb(0,0,0,0.08)] rounded-xl">
                    <SelectItem value="en" className="hover:bg-[#166B93]/10 focus:bg-[#166B93]/10 rounded-lg">
                      <Trans>English</Trans>
                    </SelectItem>
                    <SelectItem value="es" className="hover:bg-[#166B93]/10 focus:bg-[#166B93]/10 rounded-lg">
                      <Trans>Español (Spanish)</Trans>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>


            </div>

            {/* Privacy Notice */}
            <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-gray-500" />
 
                <span className="font-medium">
                  <Trans>Your data is secure</Trans>
                </span>
              </div>
              <p>
                <Trans>
                  Your information is encrypted and secure. We only use it for
                  your consultation and follow strict privacy guidelines.{" "}
                  <Link
                    to="privacy-policy"
                    target="_blank"
                    className="text-[#166B93] underline hover:text-[#166B93]/80"
                  >
                    Privacy Policy
                  </Link>
                </Trans>
 
              </p>
            </div>

            {/* Continue Button */}
            <div className="space-y-2 pt-4">
              <Button
                onClick={handleContinue}
                className="w-full shadow-[0_4px_15px_rgba(22,107,147,0.3)] hover:shadow-[0_8px_25px_rgba(22,107,147,0.4)] transform hover:-translate-y-0.5 transition-all duration-300 bg-[#166B93] hover:bg-[#105373] text-white font-bold h-16 rounded-xl text-lg"
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin mr-2" />
 
                    <Trans>Creating your profile...</Trans>
                  </>
                ) : (
                  <Trans>Begin Your Medical Journey</Trans>
                )}
 
              </Button>
              {formError ? (
                <ErrorCallout
                  message={formError}
                  status={formErrorStatus}
                  className="text-left"
                />
              ) : null}
            </div>
          </div>

          {/* Features Preview - Desktop only */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-12">
            <div className="text-center p-8 bg-gradient-to-br from-[#166B93]/5 to-[#166B93]/10 rounded-3xl border border-[#166B93]/10 hover:-translate-y-1 transition-transform duration-300">
              <div className="w-16 h-16 bg-[#166B93] rounded-full flex items-center justify-center mx-auto mb-5 shadow-lg shadow-[#166B93]/20">
                <span className="text-white text-xl font-bold">1</span>
              </div>
              <h4 className="font-heading font-bold text-gray-900 mb-2">
                <Trans>Health Assessment</Trans>
              </h4>
              <p className="body-small text-gray-600 font-medium opacity-80">
                <Trans>Comprehensive medical &amp; health questionnaire</Trans>
              </p>
            </div>
            
            <div className="text-center p-8 bg-gradient-to-br from-[#CEF12F]/10 to-[#CEF12F]/20 rounded-3xl border border-[#CEF12F]/20 hover:-translate-y-1 transition-transform duration-300">
              <div className="w-16 h-16 bg-[#CEF12F] rounded-full flex items-center justify-center mx-auto mb-5 shadow-lg shadow-[#CEF12F]/30">
                <span className="text-gray-900 text-xl font-bold">2</span>
              </div>
              <h4 className="font-heading font-bold text-gray-900 mb-2">
                <Trans>Document Upload</Trans>
              </h4>
              <p className="body-small text-gray-600 font-medium opacity-80">
                <Trans>Secure upload of medical records &amp; documents</Trans>
              </p>
            </div>
            
            <div className="text-center p-8 bg-gradient-to-br from-[#FF9142]/5 to-[#FF9142]/10 rounded-3xl border border-[#FF9142]/10 hover:-translate-y-1 transition-transform duration-300">
              <div className="w-16 h-16 bg-[#FF9142] rounded-full flex items-center justify-center mx-auto mb-5 shadow-lg shadow-[#FF9142]/20">
                <span className="text-white text-xl font-bold">3</span>
              </div>
              <h4 className="font-heading font-bold text-gray-900 mb-2">
                <Trans>Expert Consultation</Trans>
              </h4>
              <p className="body-small text-gray-600 font-medium opacity-80">
                <Trans>Personalized medical consultation &amp; treatment plan</Trans>
              </p>
            </div>
          </div>

        </div>
      </div>
      {/*  */}

    </div>
  );
}
